import groupBy from 'lodash/groupBy';

import { findSimilarRoomAssets } from '../tabs/packageOptions/roomNameMatch';
import type { CreatedAsset, RoomAssetLink } from '../tabs/packageOptions/types';

export const CREATE_ASSET = 'create';

// Excel room type to the id of the asset it loads onto, or CREATE_ASSET.
export type RoomChoices = Record<string, string>;

// Each room type starts on its closest asset that an earlier room type has not taken.
export function prefillRoomChoices(roomTypes: ReadonlyArray<string>, assets: Array<RoomAssetLink>): RoomChoices {
  const taken = new Set<string>();
  const choices: RoomChoices = {};
  for (const roomType of roomTypes) {
    const match = findSimilarRoomAssets(roomType, assets).find(({ asset }) => !taken.has(asset.id));
    choices[roomType] = match?.asset.id ?? CREATE_ASSET;
    if (match) {
      taken.add(match.asset.id);
    }
  }
  return choices;
}

// Two base rows on one asset make the forecast count that room twice.
export function roomTypesSharingAnAsset(choices: RoomChoices): Set<string> {
  const linked = Object.entries(choices).filter(([, assetId]) => assetId !== CREATE_ASSET);
  const byAsset = groupBy(linked, ([, assetId]) => assetId);
  return new Set(
    Object.values(byAsset)
      .filter((roomTypes) => roomTypes.length > 1)
      .flatMap((roomTypes) => roomTypes.map(([roomType]) => roomType)),
  );
}

export function roomTypesWithMissingAsset(choices: RoomChoices, assets: Array<RoomAssetLink>): Array<string> {
  const listed = new Set(assets.map(({ id }) => id));
  return Object.entries(choices)
    .filter(([, assetId]) => assetId !== CREATE_ASSET && !listed.has(assetId))
    .map(([roomType]) => roomType);
}

export function resolveRoomAssets(
  roomTypes: ReadonlyArray<string>,
  choices: RoomChoices,
  assets: Array<RoomAssetLink>,
): { assetByRoomType: Record<string, RoomAssetLink>; created: Array<CreatedAsset> } {
  const assetByRoomType: Record<string, RoomAssetLink> = {};
  const created: Array<CreatedAsset> = [];
  for (const roomType of roomTypes) {
    const linked = assets.find(({ id }) => id === choices[roomType]);
    if (linked) {
      assetByRoomType[roomType] = linked;
      continue;
    }
    const asset = { id: crypto.randomUUID(), name: roomType };
    created.push(asset);
    assetByRoomType[roomType] = asset;
  }
  return { assetByRoomType, created };
}
